import { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Input, message, Typography, Popconfirm, Tag, Space, Select } from 'antd';
import AppLoading from '../components/AppLoading';
import { PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined, SearchOutlined, BugOutlined } from '@ant-design/icons';
import type { DataSet } from '@dtwin/shared';
import { fetchDataSources, createDataSource, updateDataSource, deleteDataSource } from '../api/dataSources';
import { fetchPages, fetchPage } from '../api/pages';
import DataSourceConfigForm from '../components/dashboard/DataSourceConfigForm';
import DataSourceDebugDrawer from '../components/dashboard/DataSourceDebugDrawer';

const { Title, Text } = Typography;

const TYPE_LABELS: Record<string, string> = {
  static: '静态数据',
  api: 'REST API',
  websocket: 'WebSocket',
  sql: 'SQL 查询',
  mqtt: 'MQTT 订阅',
};

const TYPE_COLORS: Record<string, string> = {
  static: 'default',
  api: 'blue',
  websocket: 'green',
  sql: 'orange',
  mqtt: 'purple',
};

export default function DataSetList() {
  const [datasets, setDatasets] = useState<DataSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DataSet | null>(null);
  const [draft, setDraft] = useState<Partial<DataSet>>({ name: '', type: 'static', config: { data: '[]' } });
  const [saving, setSaving] = useState(false);
  const [refCount, setRefCount] = useState<Record<string, number>>({});
  const [debugDs, setDebugDs] = useState<DataSet | null>(null);
  // 2026-10-10（用户要求）：数据集列表搜索/排序/分页（参考可视化大屏列表）
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState<'updatedDesc' | 'updatedAsc' | 'name' | 'refs'>('updatedDesc');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = datasets.filter(
      (d) =>
        !kw ||
        (d.name || '').toLowerCase().includes(kw) ||
        (TYPE_LABELS[d.type] || d.type || '').toLowerCase().includes(kw),
    );
    switch (sortKey) {
      case 'updatedAsc': list = [...list].sort((a, b) => new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime()); break;
      case 'name': list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'zh')); break;
      case 'refs': list = [...list].sort((a, b) => (refCount[b.id] || 0) - (refCount[a.id] || 0)); break;
      default: list = [...list].sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
    }
    return list;
  }, [datasets, keyword, sortKey, refCount]);

  // 引用计数：统计所有大屏组件中 dataSource.dataSetId 的引用次数
  const loadRefCount = async () => {
    try {
      const pages = await fetchPages();
      const counts: Record<string, number> = {};
      for (const p of pages) {
        const detail = await fetchPage(p.id).catch(() => null);
        if (!detail) continue;
        for (const c of (detail.components || [])) {
          const did = (c as any)?.dataSource?.dataSetId;
          if (did) counts[did] = (counts[did] || 0) + 1;
        }
      }
      setRefCount(counts);
    } catch { /* 忽略统计失败 */ }
  };

  const loadList = () => {
    setLoading(true);
    fetchDataSources()
      .then((data) => setDatasets(data as unknown as DataSet[]))
      .catch(() => message.error('加载数据集失败'))
      .finally(() => setLoading(false));
    loadRefCount();
  };

  useEffect(() => { loadList(); }, []);

  const openCreate = () => {
    setEditing(null);
    setDraft({ name: '', type: 'static', config: { data: '[]' } });
    setModalOpen(true);
  };

  const openEdit = (ds: DataSet) => {
    setEditing(ds);
    setDraft({ ...ds, config: { ...ds.config } });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!draft.name?.trim()) { message.warning('请输入数据集名称'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateDataSource(editing.id, draft as any);
        message.success('数据集已更新');
      } else {
        await createDataSource(draft as any);
        message.success('数据集已创建');
      }
      setModalOpen(false);
      loadList();
    } catch (e: any) {
      message.error(`保存失败: ${e?.message || ''}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDataSource(id);
      message.success('已删除');
      loadList();
    } catch (e: any) {
      message.error(`删除失败: ${e?.message || ''}`);
    }
  };

  if (loading) {
    return <AppLoading />;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>数据集管理</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            先配置数据集（连接 + 查询 + 解析），组件在数据绑定面板引用，一处配置多处复用
          </Text>
        </div>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="按名称/类型搜索"
            style={{ width: 200 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            data-testid="dataset-search"
          />
          <Select
            value={sortKey}
            onChange={(v) => setSortKey(v)}
            style={{ width: 160 }}
            data-testid="dataset-sort"
            options={[
              { value: 'updatedDesc', label: '更新时间↓ 新→旧' },
              { value: 'updatedAsc', label: '更新时间↑ 旧→新' },
              { value: 'name', label: '名称排序' },
              { value: 'refs', label: '引用数（多→少）' },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建数据集</Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        dataSource={filtered}
        pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
        size="middle"
        columns={[
          { title: '名称', dataIndex: 'name', render: (v) => <Text strong>{v}</Text> },
          {
            title: '类型',
            dataIndex: 'type',
            render: (t) => <Tag color={TYPE_COLORS[t] || 'default'}>{TYPE_LABELS[t] || t}</Tag>,
          },
          {
            title: '刷新间隔',
            dataIndex: 'refreshInterval',
            width: 120,
            render: (v) => (v && v > 0 ? `${v} 秒` : '不轮询'),
          },
          {
            title: '组件引用',
            key: 'ref',
            width: 110,
            render: (_, r) => {
              const n = refCount[r.id] || 0;
              return n > 0
                ? <Tag color="green">{n} 个组件</Tag>
                : <Text type="secondary" style={{ fontSize: 12 }}>未引用</Text>;
            },
          },
          {
            title: '解析管线',
            key: 'parser',
            width: 180,
            render: (_, r) => {
              const p = r.parser as any;
              if (!p) return <Text type="secondary" style={{ fontSize: 12 }}>无</Text>;
              const parts = [p.path && `取值`, p.mapping?.length && `映射${p.mapping.length}`, p.filter && `过滤`, p.transform && `转换`].filter(Boolean);
              return <Text style={{ fontSize: 12 }}>{parts.join(' + ') || '有解析'}</Text>;
            },
          },
          { title: '更新时间', dataIndex: 'updatedAt', width: 170, render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
          {
            title: '操作',
            key: 'op',
            width: 150,
            render: (_, r) => (
              <Space size="small">
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
                <Button size="small" icon={<BugOutlined />} onClick={() => setDebugDs(r)} data-testid={`dataset-debug-${r.id}`}>调试</Button>
                <Popconfirm
                  title={refCount[r.id] ? `该数据集被 ${refCount[r.id]} 个组件引用，删除后这些组件将无数据，确认删除？` : '确认删除？'}
                  onConfirm={() => handleDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? '编辑数据集' : '新建数据集'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={520}
        destroyOnHidden
      >
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>数据集名称</Text>
          <Input
            placeholder="如：设备实时温度"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <DataSourceConfigForm
          value={draft as any}
          onChange={(ds) => setDraft({ ...draft, ...(ds as any) })}
          extra={
            <div style={{ marginTop: 8 }}>
              <Text style={{ fontSize: 11, color: '#888' }}>
                <ApiOutlined /> 组件引用此数据集后，修改此处连接/解析，所有引用组件同步生效
              </Text>
            </div>
          }
        />
      </Modal>

      <DataSourceDebugDrawer
        open={!!debugDs}
        dataset={debugDs}
        onClose={() => setDebugDs(null)}
        onSaved={loadList}
      />
    </div>
  );
}
